// engine-exception: portal pre-auth pages — sign-in, invitation acceptance (POST /portal/auth/accept), and the signed-link actions (CSAT score, one-click approval) that work without a session unless the tenant requires login for links
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ApiError } from '../../engine/api/client';
import { useApi } from '../../engine/api/context';
import { Button } from '../../engine/design/primitives/Button';
import { Field, Input } from '../../engine/design/primitives/Input';
import { Banner, EmptyState, Loading } from '../../engine/design/primitives/Misc';
import { portalAuthConfigured, portalHaloBegin, portalHaloComplete, portalHaloRoute, portalResetPassword, portalSignIn, portalToken } from '../auth';
import type { AcceptResponse } from '../types';

function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="pt-auth">
      <div className="pt-auth-card">
        <div className="hf-caps">Customer portal</div>
        <h1>{title}</h1>
        {subtitle && <p className="hf-muted" style={{ marginTop: 0 }}>{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}

const detail = (e: unknown) => (e instanceof ApiError ? e.detail : e instanceof Error ? e.message : String(e));

// ── Sign in ───────────────────────────────────────────────────────────────────
export function LoginPage() {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/portal';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Already signed in? Straight through; the shell decides whether this token is a portal user.
  useEffect(() => {
    void portalToken().then((t) => { if (t) navigate(from, { replace: true }); });
  }, [from, navigate]);
  // An organisation on its HaloPSA signs its customers in with their Halo login.
  const [halo, setHalo] = useState<{ tenantId: string; organisation: string | null } | null>(null);
  useEffect(() => {
    let live = true;
    void portalHaloRoute().then((r) => { if (live && r.sso && r.tenantId) setHalo({ tenantId: r.tenantId, organisation: r.organisation }); });
    return () => { live = false; };
  }, []);
  const haloSignIn = async () => {
    if (!halo) return;
    setError(null);
    setBusy(true);
    try {
      await portalHaloBegin(halo.tenantId);
    } catch (err) {
      setError(detail(err));
      setBusy(false);
    }
  };

  // Email first: the address decides the sign-in (/portal/auth/discover) — an organisation whose
  // customers sign in with their Halo login gets that; everyone else gets a password.
  const [step, setStep] = useState<'email' | 'password'>('email');
  const discover = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const r = await api.request<{ method: 'halo' | 'password'; tenant_id: string | null; organisation: string | null }>('/auth/discover', { query: { email: email.trim() } });
      if (r.method === 'halo' && r.tenant_id) setHalo({ tenantId: r.tenant_id, organisation: r.organisation });
      else setStep('password');
    } catch {
      setStep('password'); // discovery is a convenience; the password form always works
    } finally {
      setBusy(false);
    }
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await portalSignIn(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(detail(err));
    } finally {
      setBusy(false);
    }
  };
  const reset = async () => {
    setError(null);
    try {
      await portalResetPassword(email.trim());
      setNotice(`If ${email.trim()} has portal access, a reset link is on its way.`);
    } catch (err) {
      setError(detail(err));
    }
  };
  return (
    <Card title="Sign in" subtitle="Use the email address your invitation was sent to.">
      {halo && (
        <div className="hf-stack" style={{ marginBottom: 'var(--s-4)' }}>
          <Button variant="primary" block busy={busy} onClick={() => void haloSignIn()}>Sign in with Halo</Button>
          <p className="hf-muted" style={{ margin: 0, fontSize: 'var(--t-12)' }}>Use the login you have for {halo.organisation ?? 'your provider'}’s Halo portal.</p>
        </div>
      )}
      {!portalAuthConfigured && !halo && <Banner tone="warn">Sign-in is not configured on this installation. Open the link from your invitation email instead.</Banner>}
      {step === 'email' ? (
        <form className="hf-stack" onSubmit={discover}>
          <Field label="Email" required><Input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus /></Field>
          {error && <Banner tone="error">{error}</Banner>}
          <Button type="submit" variant={halo ? 'outline' : 'primary'} block busy={busy} disabled={!/^[^@\s]+@[^@\s]+$/.test(email.trim())}>Continue</Button>
        </form>
      ) : (
        <form className="hf-stack" onSubmit={submit}>
          <Field label="Email" required>
            <div className="hf-row" style={{ gap: 'var(--s-2)' }}>
              <Input type="email" autoComplete="email" value={email} readOnly aria-readonly="true" />
              <Button variant="ghost" size="sm" onClick={() => { setStep('email'); setPassword(''); }}>Change</Button>
            </div>
          </Field>
          <Field label="Password" required><Input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus /></Field>
          {error && <Banner tone="error">{error}</Banner>}
          {notice && <Banner tone="pos">{notice}</Banner>}
          <Button type="submit" variant="primary" block busy={busy} disabled={!email.trim() || !password}>Sign in</Button>
          <button type="button" className="hf-link" style={{ font: 'inherit', fontSize: 'var(--t-12)' }} onClick={() => void reset()} disabled={!email.trim()}>Forgotten your password?</button>
        </form>
      )}
    </Card>
  );
}

// ── Back from Halo ────────────────────────────────────────────────────────────
export function HaloCallbackPage() {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const code = sp.get('code');
    const state = sp.get('state');
    if (!code || !state) {
      setError(sp.get('error_description') ?? 'Halo did not complete the sign-in.');
      return;
    }
    portalHaloComplete(code, state)
      .then(() => navigate('/portal', { replace: true }))
      .catch((err: unknown) => setError(detail(err)));
  }, [navigate, sp]);
  return (
    <Card title={error ? 'Sign-in failed' : 'Signing you in…'}>
      {error ? (
        <div className="hf-stack">
          <Banner tone="error">{error}</Banner>
          <Link to="/portal/login" className="hf-link">Try again</Link>
        </div>
      ) : (
        <Loading />
      )}
    </Card>
  );
}

// ── Accept invitation ─────────────────────────────────────────────────────────
export function AcceptPage() {
  const api = useApi();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const accept = useMutation<AcceptResponse, ApiError, void>({
    mutationFn: async () => {
      const bearer = await portalToken();
      return api.request<AcceptResponse>('/auth/accept', { method: 'POST', body: { token, password: password || undefined }, headers: bearer ? { authorization: `Bearer ${bearer}` } : undefined });
    },
    onSuccess: async (r) => {
      if (r.sign_in === 'gotrue' && password) {
        try {
          await portalSignIn(r.email, password);
          navigate('/portal', { replace: true });
          return;
        } catch {
          /* fall through to the confirmation with a sign-in link */
        }
      }
      if (r.sign_in === 'bearer') navigate('/portal', { replace: true });
    },
  });
  if (!token) return <Card title="Invitation link missing"><p>Open the link from your invitation email; it carries the token that activates your access.</p></Card>;
  if (accept.data && accept.data.sign_in !== 'bearer') {
    return (
      <Card title="You're all set" subtitle={`Your access to ${accept.data.tenant.name}'s portal is active.`}>
        {accept.data.sign_in === 'subject' ? (
          <Banner tone="info">This installation has no password sign-in yet. Your provider will let you know how to sign in.</Banner>
        ) : (
          <Link className="hf-btn primary" to="/portal/login">Sign in</Link>
        )}
      </Card>
    );
  }
  const mismatch = password.length > 0 && confirm.length > 0 && password !== confirm;
  const err = accept.error;
  return (
    <Card title="Set up your portal access" subtitle="Choose a password to finish activating your invitation.">
      <form className="hf-stack" onSubmit={(e) => { e.preventDefault(); if (!mismatch) accept.mutate(); }}>
        <Field label="Password" required help="At least 8 characters."><Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} autoFocus minLength={8} /></Field>
        <Field label="Confirm password" required error={mismatch ? 'Passwords do not match' : undefined}><Input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} invalid={mismatch} /></Field>
        {err && <Banner tone="error">{err.status === 404 ? 'This invitation link is not valid.' : err.status === 410 ? 'This invitation has expired — ask your provider to send a new one.' : err.status === 409 ? 'This invitation was already used. Sign in instead.' : err.detail}</Banner>}
        <Button type="submit" variant="primary" block busy={accept.isPending} disabled={password.length < 8 || mismatch}>Activate</Button>
        <p className="hf-muted" style={{ fontSize: 'var(--t-12)', margin: 0 }}>Already have access? <Link to="/portal/login">Sign in</Link></p>
      </form>
    </Card>
  );
}

// ── Signed links ──────────────────────────────────────────────────────────────
export function CsatLinkPage() {
  const api = useApi();
  const [sp] = useSearchParams();
  const token = sp.get('token') ?? '';
  const initial = Number(sp.get('score') ?? '');
  const [score, setScore] = useState<number>(Number.isInteger(initial) && initial >= 1 && initial <= 5 ? initial : 0);
  const [comment, setComment] = useState('');
  const submit = useMutation<unknown, ApiError, void>({ mutationFn: () => api.request('/public/csat', { method: 'POST', body: { token, score, comment: comment || undefined } }) });
  if (!token) return <Card title="Rating link missing"><p>Open the link from your email.</p></Card>;
  if (submit.isSuccess) return <Card title="Thank you" subtitle="Your rating has been recorded."><Link className="hf-btn" to="/portal">Open the portal</Link></Card>;
  const err = submit.error;
  return (
    <Card title="How did we do?" subtitle="Rate the help you received on this ticket.">
      <div className="hf-stack">
        <div className="pt-stars" role="radiogroup" aria-label="Score">
          {[1, 2, 3, 4, 5].map((n) => <button key={n} type="button" role="radio" aria-checked={score === n} aria-pressed={n <= score} aria-label={`${n} star${n === 1 ? '' : 's'}`} onClick={() => setScore(n)}>★</button>)}
        </div>
        <Field label="Comment (optional)"><Input value={comment} onChange={(e) => setComment(e.target.value)} /></Field>
        {err && <Banner tone="error">{err.status === 403 && /login|sign/i.test(err.detail) ? <>This link needs you to be signed in. <Link to="/portal/login" state={{ from: `/portal/csat?token=${encodeURIComponent(token)}&score=${score}` }}>Sign in</Link></> : err.detail}</Banner>}
        <Button variant="primary" block busy={submit.isPending} disabled={score === 0} onClick={() => submit.mutate()}>Send rating</Button>
      </div>
    </Card>
  );
}

export function ApproveLinkPage() {
  const api = useApi();
  const [sp] = useSearchParams();
  const token = sp.get('token') ?? '';
  const tenant = sp.get('t') ?? '';
  const run = useMutation<{ state?: string; decision?: string } & Record<string, unknown>, ApiError, void>({ mutationFn: () => api.request('/public/approvals/one-click', { method: 'POST', body: { tenant_id: tenant, token } }) });
  useEffect(() => {
    if (token && tenant) run.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, tenant]);
  if (!token || !tenant) return <Card title="Approval link missing"><p>Open the link from your email.</p></Card>;
  if (run.isPending || run.isIdle) return <Card title="Recording your decision…"><Loading /></Card>;
  if (run.error) {
    const e = run.error;
    const needLogin = e.status === 403 && /login|sign/i.test(e.detail);
    return (
      <Card title={needLogin ? 'Sign in to continue' : 'Could not record your decision'}>
        <EmptyState title={needLogin ? 'This link needs you to be signed in.' : e.status === 404 || e.status === 410 ? 'This link is no longer valid.' : e.detail} icon={needLogin ? 'lock' : 'alert-circle'} action={<Link className="hf-btn primary" to={needLogin ? '/portal/login' : '/portal/approvals'} state={needLogin ? { from: `/portal/approvals` } : undefined}>{needLogin ? 'Sign in' : 'Open approvals'}</Link>} />
      </Card>
    );
  }
  return (
    <Card title="Decision recorded" subtitle="Thank you — the requester has been notified.">
      <Link className="hf-btn" to="/portal/approvals">Open the portal</Link>
    </Card>
  );
}
